/**
 * @fileoverview GameState パス定数定義
 *
 * 全ファイル共通で使用するGameStateパスを列挙
 * typo防止、IDE支援、検索性向上のため
 *
 * 使用例: STATE_PATHS.〇〇 を参照
 */

/**
 * GameState パス定数オブジェクト
 * Object.freezeによる不変化
 */
export const STATE_PATHS = Object.freeze({
  // ========================================
  // Core（コアゲーム状態）
  // ========================================
  /** @type {'core.status'} ゲームステータス（'title'|'ready'|'processing'|'paused'|'gameover'） */
  CORE_STATUS: 'core.status',

  // ========================================
  // UI（ユーザーインターフェース状態）
  // ========================================
  /** @type {'ui'} UI状態全体 */
  UI: 'ui',

  /** @type {'ui.isSkillTargeting'} スキルターゲティング中フラグ */
  UI_IS_SKILL_TARGETING: 'ui.isSkillTargeting',

  /** @type {'ui.remainingSkillUses'} 残りスキル使用数 */
  UI_REMAINING_SKILL_USES: 'ui.remainingSkillUses',

  /** @type {'ui.isDrawerOpen'} 設定ドロワー開閉フラグ */
  UI_IS_DRAWER_OPEN: 'ui.isDrawerOpen',

  /** @type {'ui.tutorial'} チュートリアルUI状態 */
  UI_TUTORIAL: 'ui.tutorial',

  /** @type {'ui.tutorial.status'} チュートリアル進行ステータス */
  UI_TUTORIAL_STATUS: 'ui.tutorial.status',

  /** @type {'ui.tutorial.step'} チュートリアル現在ステップID */
  UI_TUTORIAL_STEP: 'ui.tutorial.step',

  // ========================================
  // Game（ゲームデータ）
  // ========================================
  /** @type {'game.score'} 現在のスコア */
  GAME_SCORE: 'game.score',

  // ========================================
  // Settings（設定）
  // ========================================
  /** @type {'settings'} ユーザー設定全体 */
  SETTINGS: 'settings',

  /** @type {'settings.gameMode'} ゲームモード（'Blitz'|'Classic'） */
  SETTINGS_GAME_MODE: 'settings.gameMode',

  /** @type {'settings.selectedSkill'} 選択中スキル */
  SETTINGS_SELECTED_SKILL: 'settings.selectedSkill',

  /** @type {'settings.tutorialOnboarding'} チュートリアル設定全体 */
  SETTINGS_TUTORIAL_ONBOARDING: 'settings.tutorialOnboarding',

  /** @type {'settings.tutorialOnboarding.status'} チュートリアル完了状態 */
  SETTINGS_TUTORIAL_ONBOARDING_STATUS: 'settings.tutorialOnboarding.status',

  /** @type {'settings.tutorialOnboarding.completedAt'} チュートリアル完了日時 */
  SETTINGS_TUTORIAL_ONBOARDING_COMPLETED_AT: 'settings.tutorialOnboarding.completedAt',

  // ========================================
  // Stats（統計）
  // ========================================
  /** @type {'stats'} 統計データ全体 */
  STATS: 'stats',

  /** @type {'stats.playCount'} プレイ回数 */
  STATS_PLAY_COUNT: 'stats.playCount',

  /** @type {'stats.bestScore'} ベストスコア */
  STATS_BEST_SCORE: 'stats.bestScore',

  /** @type {'stats.longestChain'} 最長チェイン */
  STATS_LONGEST_CHAIN: 'stats.longestChain',

  /** @type {'stats.bestMatch'} 最大マッチ数 */
  STATS_BEST_MATCH: 'stats.bestMatch',

  // ========================================
  // Limit System（制限システム - 制限周り）
  // ========================================
  /** @type {'limit'} 制限システム全体 */
  LIMIT: 'limit',

  // --- Config ---
  /** @type {'limit.config'} 制限設定 */
  LIMIT_CONFIG: 'limit.config',

  /** @type {'limit.config.timeLimitMs'} 時間制限（ミリ秒） */
  LIMIT_CONFIG_TIME_LIMIT_MS: 'limit.config.timeLimitMs',

  /** @type {'limit.config.maxMoves'} 最大手数 */
  LIMIT_CONFIG_MAX_MOVES: 'limit.config.maxMoves',

  // --- Runtime ---
  /** @type {'limit.runtime'} 実行時状態 */
  LIMIT_RUNTIME: 'limit.runtime',

  /** @type {'limit.runtime.phase'} 制限フェーズ（'idle'|'intro'|'running'|'consumed'） */
  LIMIT_RUNTIME_PHASE: 'limit.runtime.phase',

  /** @type {'limit.runtime.remainingValue'} 残り値（時間=秒／手数=回） */
  LIMIT_RUNTIME_REMAINING_VALUE: 'limit.runtime.remainingValue',

  /** @type {'limit.runtime.lastLoggedSecond'} 直近に記録した秒 */
  LIMIT_RUNTIME_LAST_LOGGED_SECOND: 'limit.runtime.lastLoggedSecond',

  /** @type {'limit.runtime.introProgress'} イントロ進捗（0-1） */
  LIMIT_RUNTIME_INTRO_PROGRESS: 'limit.runtime.introProgress',

  /** @type {'limit.runtime.totalActiveMs'} 累計稼働時間（ミリ秒） */
  LIMIT_RUNTIME_TOTAL_ACTIVE_MS: 'limit.runtime.totalActiveMs',

  // --- Callback（コールバック登録用）---
  /** @type {'limit.onLimitReached'} 制限到達時コールバック */
  LIMIT_ON_LIMIT_REACHED: 'limit.onLimitReached',
});
